import { createFileRoute } from "@tanstack/react-router";
import { Landing } from "@/components/landing";
import { SITE_URL } from "@/lib/site-url";

export const Route = createFileRoute("/")({
  component: Landing,
  head: () => ({
    meta: [
      { title: "SMS Verification & Disposable Email — SmsEmpire" },
      {
        name: "description",
        content:
          "Receive SMS codes online instantly with virtual numbers from 200+ services. Generate disposable emails on Gmail, Outlook, iCloud & Yandex. Buy verified accounts with custom followers. Pay-as-you-go, no subscription.",
      },
      {
        name: "keywords",
        content:
          "sms verification, receive sms online, temporary phone number, virtual number, disposable email, temp mail, temporary email, buy accounts, instagram accounts, telegram verification, whatsapp verification, discord verification, otp online",
      },
      { property: "og:title", content: "SMS Verification & Disposable Email — SmsEmpire" },
      {
        property: "og:description",
        content:
          "Instant SMS codes, disposable emails on real domains and ready-to-use verified accounts. Trusted worldwide.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: `${SITE_URL}/` },
      { property: "og:image", content: `${SITE_URL}/og/default.png` },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { property: "og:image:alt", content: "SmsEmpire — SMS Verification & Disposable Email" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "SMS Verification & Disposable Email — SmsEmpire" },
      { name: "twitter:image", content: `${SITE_URL}/og/default.png` },
      {
        name: "twitter:description",
        content:
          "Virtual numbers, disposable emails and verified accounts — instant and worldwide.",
      },
    ],
    links: [
      { rel: "canonical", href: `${SITE_URL}/` },
      { rel: "alternate", hrefLang: "x-default", href: `${SITE_URL}/` },
      { rel: "alternate", hrefLang: "en", href: `${SITE_URL}/en` },
      { rel: "alternate", hrefLang: "es", href: `${SITE_URL}/es` },
      { rel: "alternate", hrefLang: "fr", href: `${SITE_URL}/fr` },
      { rel: "alternate", hrefLang: "de", href: `${SITE_URL}/de` },
      { rel: "alternate", hrefLang: "it", href: `${SITE_URL}/it` },
      { rel: "alternate", hrefLang: "pt", href: `${SITE_URL}/pt` },
      { rel: "alternate", hrefLang: "ru", href: `${SITE_URL}/ru` },
      { rel: "alternate", hrefLang: "zh", href: `${SITE_URL}/zh` },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebSite",
          name: "SmsEmpire",
          url: `${SITE_URL}/`,
          potentialAction: {
            "@type": "SearchAction",
            target: `${SITE_URL}/dashboard?q={query}`,
            "query-input": "required name=query",
          },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          serviceType: "SMS verification and disposable email",
          provider: {
            "@type": "Organization",
            name: "SmsEmpire",
            url: `${SITE_URL}/`,
          },
          areaServed: "Worldwide",
          description:
            "Rent virtual phone numbers to receive SMS one-time codes, generate disposable email inboxes on real domains, and buy pre-made verified accounts.",
          offers: {
            "@type": "AggregateOffer",
            priceCurrency: "USD",
            lowPrice: "0.35",
            highPrice: "50.00",
            offerCount: "200",
          },
          // aggregateRating intentionally omitted — no on-page review widget exists.
          // Add back only when real reviews are displayed (SEO-010).
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: [
            {
              "@type": "Question",
              name: "What is SMS verification and how does SmsEmpire work?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "SMS verification is when a service sends a one-time code to a phone number to confirm ownership. SmsEmpire rents you a temporary virtual number for the exact service you need (Telegram, WhatsApp, Discord and 200+ more). You submit the number, receive the code inside your dashboard within seconds, and pay only for successful codes.",
              },
            },
            {
              "@type": "Question",
              name: "Are the disposable emails on real domains?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Yes. SmsEmpire generates disposable inboxes on real, deliverable domains including Gmail, Outlook, Hotmail, iCloud and Yandex (plus 20+ regional Yandex TLDs), so signup forms accept them like a normal address.",
              },
            },
            {
              "@type": "Question",
              name: "How fast do SMS codes arrive?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Most codes arrive in under 30 seconds. If a code never arrives, the rental is cancelled and your balance is refunded automatically.",
              },
            },
            {
              "@type": "Question",
              name: "Can I buy pre-made social accounts with followers?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Yes. Instagram, X (Twitter) and Facebook accounts can be purchased with a custom follower count. The price scales transparently per follower and the account is delivered instantly to your inbox.",
              },
            },
            {
              "@type": "Question",
              name: "Do I need a subscription?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "No. SmsEmpire is pay-as-you-go — top up your balance once and spend it whenever you need a code, an inbox or an account. No monthly fees.",
              },
            },
            {
              "@type": "Question",
              name: "Is SmsEmpire legal to use?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Yes for standard signup verification, testing and privacy use cases. SmsEmpire forbids use for government services, banking, or any KYC-regulated identity verification.",
              },
            },
          ],
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
          ],
        }),
      },
    ],
  }),
} as any);
